import { useEffect, useState } from 'react';

import BpkSelectableChip from '@skyscanner-internal/backpack-web/bpk-component-chip';
import InformationCircleIconSm from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/information-circle';
import BpkMobileScrollContainer from '@skyscanner-internal/backpack-web/bpk-component-mobile-scroll-container';
import BpkPageIndicator from '@skyscanner-internal/backpack-web/bpk-component-page-indicator';
import BpkPanel from '@skyscanner-internal/backpack-web/bpk-component-panel';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import classNames from 'classnames';
import reactReplacePlaceholder from 'react-replace-placeholder';

import MobileScrollContainer from '../../common/MobileScrollContainer';
import FlightStatusBadge from '../FlightStatusBadge';
import {
  getScheduledArrivalAirportCode,
  getScheduledArrivalAirportName,
} from '../flightStatusUpdate';
import {
  formatBrowserLocalDateKey,
  getBestArrivalTime,
  getBestDepartureTime,
  parseDateKeyToUTCDate,
} from '../utils';

import type { NetSkyscannerArrivaldeparturesvcV1ArrivalDepartureService as ArrivalDepartureTypes } from '@skyscanner-internal/arrival-departure-svc-web-http-client';
import type { FlightStatusDetailsProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/FlightStatusDetailsProps';

import STYLES from './FlightStatusMultipleFlights.module.scss';

type FlightStatusUpdate = ArrivalDepartureTypes.FlightStatusUpdate;

const formatDateChipLabel = (
  dateKey: string,
  locale: string,
  todayDateKey: string,
  strings: FlightStatusDetailsProps['strings'],
): string => {
  const date = parseDateKeyToUTCDate(dateKey);

  if (!date) {
    return dateKey;
  }

  const baseLabel = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(date);

  if (dateKey === todayDateKey) {
    return strings.FlightTrackerFlight_other_flights_tab_today.replaceAll(
      '@@day_date@@',
      baseLabel,
    );
  }

  return baseLabel;
};

const formatAirportDisplay = (airportName?: string, airportCode?: string) => {
  if (airportName && airportCode) {
    return `${airportName} (${airportCode})`;
  }

  return airportName || airportCode || null;
};

export interface FlightStatusMultipleFlightsProps {
  /** The list of flights for the selected date */
  selectedDateFlights: FlightStatusUpdate[];
  /** The currently selected flight */
  selectedFlight: FlightStatusUpdate;
  /** The index of the currently selected flight */
  selectedFlightIndex: number;
  /** The list of visible date keys (YYYY-MM-DD format) */
  visibleDateKeys: string[];
  /** The currently selected date key (YYYY-MM-DD format) */
  effectiveDateKey: string;
  /** The requested flight code (e.g., BA123) */
  requestedFlightCode: string;
  /** The user's locale for date/time formatting */
  locale: string;
  /** Translation strings */
  strings: FlightStatusDetailsProps['strings'];
  /** Callback when a date is selected */
  onDateSelect: (dateKey: string) => void;
  /** Callback when a flight is selected by index */
  onFlightSelect: (flightIndex: number) => void;
}

/**
 * FlightStatusMultipleFlights: Displays date navigation and flight selector when multiple flights exist.
 *
 * Renders a single HTML tree; CSS media queries switch between a mobile scroll-snap card view
 * and a desktop tabular row view. No JS breakpoint branching.
 */
const FlightStatusMultipleFlights = ({
  effectiveDateKey,
  locale,
  onDateSelect,
  onFlightSelect,
  requestedFlightCode,
  selectedDateFlights,
  selectedFlight,
  selectedFlightIndex,
  strings,
  visibleDateKeys,
}: FlightStatusMultipleFlightsProps) => {
  // UAT 10.1 / CASTLE-4926: the "Today" chip must match today in the user's
  // browser timezone, not UTC. Chip keys are already airport-local for
  // flights with localised times, so the "Today" label lights up when the
  // user's current browser date matches any chip bucket.
  const todayDateKey = formatBrowserLocalDateKey(new Date());
  const [visibleMobileFlightIndex, setVisibleMobileFlightIndex] =
    useState(selectedFlightIndex);

  useEffect(() => {
    setVisibleMobileFlightIndex(selectedFlightIndex);
  }, [selectedFlightIndex]);

  const renderDateChips = () =>
    visibleDateKeys.map((dateKey) => (
      <BpkSelectableChip
        accessibilityLabel={formatDateChipLabel(
          dateKey,
          locale,
          todayDateKey,
          strings,
        )}
        className={STYLES.FlightStatusMultipleFlights__dateButton}
        key={dateKey}
        name={dateKey}
        onClick={() => onDateSelect(dateKey)}
        selected={dateKey === effectiveDateKey}
      >
        {formatDateChipLabel(dateKey, locale, todayDateKey, strings)}
      </BpkSelectableChip>
    ));

  return (
    <section className={STYLES.FlightStatusMultipleFlights}>
      <BpkText tagName="h3" textStyle={TEXT_STYLES.heading3}>
        {reactReplacePlaceholder(
          strings.FlightTrackerFlight_other_flights_h2,
          {
            flight_number: requestedFlightCode,
          },
          '@@',
          '@@',
        )}
      </BpkText>
      <BpkText
        textStyle={TEXT_STYLES.footnote}
        className={STYLES.FlightStatusMultipleFlights__multiFlightInfo}
      >
        <span
          className={STYLES.FlightStatusMultipleFlights__multiFlightInfoIcon}
        >
          <InformationCircleIconSm />
        </span>
        {reactReplacePlaceholder(
          strings.FlightTrackerFlight_other_flights_message,
          {
            flight_number: requestedFlightCode,
          },
          '@@',
          '@@',
        )}
      </BpkText>

      <div className={STYLES.FlightStatusMultipleFlights__dateButtons}>
        <BpkMobileScrollContainer>{renderDateChips()}</BpkMobileScrollContainer>
      </div>

      <div className={STYLES.FlightStatusMultipleFlights__flightListHeader}>
        <span
          className={classNames(
            STYLES.FlightStatusMultipleFlights__flightRowCell,
            STYLES.FlightStatusMultipleFlights__flightRowFrom,
          )}
        >
          {strings.FlightTrackerFlight_other_flights_from_label}
        </span>
        <span
          className={classNames(
            STYLES.FlightStatusMultipleFlights__flightRowCell,
            STYLES.FlightStatusMultipleFlights__flightRowTo,
          )}
        >
          {strings.FlightTrackerFlight_other_flights_to_label}
        </span>
        <span
          className={classNames(
            STYLES.FlightStatusMultipleFlights__flightRowCell,
            STYLES.FlightStatusMultipleFlights__flightRowDeparts,
          )}
        >
          {strings.FlightTrackerFlight_other_flights_departs_label}
        </span>
        <span
          className={classNames(
            STYLES.FlightStatusMultipleFlights__flightRowCell,
            STYLES.FlightStatusMultipleFlights__flightRowArrives,
          )}
        >
          {strings.FlightTrackerFlight_other_flights_arrives_label}
        </span>
        <span
          className={classNames(
            STYLES.FlightStatusMultipleFlights__flightRowCell,
            STYLES.FlightStatusMultipleFlights__flightRowStatus,
          )}
        >
          {strings.FlightTrackerFlight_other_flights_status_label}
        </span>
      </div>

      <div className={STYLES.FlightStatusMultipleFlights__flightList}>
        <MobileScrollContainer
          className={STYLES.FlightStatusMultipleFlights__flightScroller}
          selectedItem={selectedFlightIndex}
          setVisibleIndex={setVisibleMobileFlightIndex}
          onScrollComplete={onFlightSelect}
        >
          {selectedDateFlights.map((flight, index) => {
            const isSelected =
              flight.scheduleInstanceKey ===
              selectedFlight?.scheduleInstanceKey;
            const bestDepartureTime = getBestDepartureTime(
              flight,
              strings,
              locale,
            );
            const bestArrivalTime = getBestArrivalTime(flight, strings, locale);

            return (
              <div
                className={STYLES.FlightStatusMultipleFlights__flightCard}
                key={flight.scheduleInstanceKey}
              >
                <button
                  aria-label={`${requestedFlightCode} ${bestDepartureTime.value}`}
                  aria-pressed={isSelected}
                  className={STYLES.FlightStatusMultipleFlights__flightButton}
                  onClick={() => onFlightSelect(index)}
                  type="button"
                >
                  <BpkPanel
                    className={classNames(
                      STYLES.FlightStatusMultipleFlights__flightRowCard,
                      {
                        [STYLES[
                          'FlightStatusMultipleFlights__flightRowCard--selected'
                        ]]: isSelected,
                      },
                    )}
                  >
                    <div
                      className={
                        STYLES.FlightStatusMultipleFlights__flightRowGrid
                      }
                    >
                      <div
                        className={classNames(
                          STYLES.FlightStatusMultipleFlights__flightRowCell,
                          STYLES.FlightStatusMultipleFlights__flightRowFrom,
                        )}
                      >
                        <strong
                          className={
                            STYLES.FlightStatusMultipleFlights__flightRowLabel
                          }
                        >
                          {strings.FlightTrackerFlight_other_flights_from_label}
                        </strong>
                        <span>
                          {formatAirportDisplay(
                            flight.departureAirportName,
                            flight.departureAirportCode,
                          ) ||
                            strings.FlightTrackerFlight_arrival_not_available}
                        </span>
                      </div>
                      <div
                        className={classNames(
                          STYLES.FlightStatusMultipleFlights__flightRowCell,
                          STYLES.FlightStatusMultipleFlights__flightRowTo,
                        )}
                      >
                        <strong
                          className={
                            STYLES.FlightStatusMultipleFlights__flightRowLabel
                          }
                        >
                          {strings.FlightTrackerFlight_other_flights_to_label}
                        </strong>
                        <span>
                          {formatAirportDisplay(
                            getScheduledArrivalAirportName(flight),
                            getScheduledArrivalAirportCode(flight),
                          )}
                        </span>
                      </div>
                      <div
                        className={classNames(
                          STYLES.FlightStatusMultipleFlights__flightRowCell,
                          STYLES.FlightStatusMultipleFlights__flightRowDeparts,
                        )}
                      >
                        <strong
                          className={
                            STYLES.FlightStatusMultipleFlights__flightRowLabel
                          }
                        >
                          {
                            strings.FlightTrackerFlight_other_flights_departs_label
                          }
                        </strong>
                        <span>{bestDepartureTime.value}</span>
                      </div>
                      <div
                        className={classNames(
                          STYLES.FlightStatusMultipleFlights__flightRowCell,
                          STYLES.FlightStatusMultipleFlights__flightRowArrives,
                        )}
                      >
                        <strong
                          className={
                            STYLES.FlightStatusMultipleFlights__flightRowLabel
                          }
                        >
                          {
                            strings.FlightTrackerFlight_other_flights_arrives_label
                          }
                        </strong>
                        <span>{bestArrivalTime.value}</span>
                      </div>
                      <div
                        className={classNames(
                          STYLES.FlightStatusMultipleFlights__flightRowCell,
                          STYLES.FlightStatusMultipleFlights__flightRowStatus,
                        )}
                      >
                        <strong
                          className={
                            STYLES.FlightStatusMultipleFlights__flightRowLabel
                          }
                        >
                          {
                            strings.FlightTrackerFlight_other_flights_status_label
                          }
                        </strong>
                        <span>
                          <FlightStatusBadge
                            flight={flight}
                            strings={strings}
                          />
                        </span>
                      </div>
                    </div>
                  </BpkPanel>
                </button>
              </div>
            );
          })}
        </MobileScrollContainer>

        {selectedDateFlights.length > 1 && (
          <div
            className={STYLES.FlightStatusMultipleFlights__mobilePageIndicator}
          >
            <BpkPageIndicator
              currentIndex={visibleMobileFlightIndex}
              totalIndicators={selectedDateFlights.length}
              onClick={(_event, newIndex) => {
                setVisibleMobileFlightIndex(newIndex);
                onFlightSelect(newIndex);
              }}
              indicatorLabel="Go to flight"
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default FlightStatusMultipleFlights;
